import React, {useState} from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import BackOfficeMenu from '../components/BackOfficeMenu';
import {usePermissions} from '../../utils/usePermissions';
import './DetailDemandesDevis.css';

const API_URL = import.meta.env.VITE_API_URL;

const DetailDemandesDevis = ({demande: initialDemande, onBack, onNavigate, onLogout, currentPage}) => {
  const {hasPermission} = usePermissions();
  const [demande, setDemande] = useState(initialDemande);
  const [uploadingContract, setUploadingContract] = useState(false);

  const getStatutColor = (statut) => {
    const colors = {
      'en_cours':   '#17a2b8',
      'en_attente': '#ffc107',
      'validé':     '#28a745',
      'rejeté':     '#dc3545'
    };
    return colors[statut] || '#6c757d';
  };

  const getStatutLabel = (statut) => {
    const labels = {
      'en_cours':   'En Cours',
      'en_attente': 'En Attente',
      'validé':     'Validé',
      'rejeté':     'Rejeté'
    };
    return labels[statut] || statut;
  };

  const formatDate = (date) =>
    new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });

  const formatDateLong = (date) =>
    new Date(date).toLocaleDateString('fr-FR', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
    });

  const refreshDemande = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/demandes-devis`, {
        headers: {Authorization: `Bearer ${token}`}
      });
      const updated = res.data.find(d => d._id === demande._id);
      if (updated) setDemande(updated);
    } catch (e) {
      console.error('Erreur rafraîchissement', e);
    }
  };

  const handleValider = async () => {
    const {value: commentaire} = await Swal.fire({
      title: 'Valider la demande',
      input: 'textarea',
      inputLabel: 'Commentaire (optionnel)',
      inputPlaceholder: 'Entrez un commentaire pour le client...',
      showCancelButton: true,
      confirmButtonText: 'Valider',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#28a745'
    });
    if (commentaire !== undefined) {
      try {
        const token = localStorage.getItem('token');
        await axios.put(`${API_URL}/demandes-devis/${demande._id}/valider`,
          {commentaireAdmin: commentaire},
          {headers: {Authorization: `Bearer ${token}`}}
        );
        Swal.fire('Succès', 'Demande validée avec succès', 'success');
        await refreshDemande();
      } catch (e) {
        Swal.fire('Erreur', 'Impossible de valider la demande', 'error');
      }
    }
  };

  const handleRejeter = async () => {
    const {value: commentaire} = await Swal.fire({
      title: 'Rejeter la demande',
      input: 'textarea',
      inputLabel: 'Motif du rejet',
      inputPlaceholder: 'Entrez le motif du rejet...',
      showCancelButton: true,
      confirmButtonText: 'Rejeter',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#dc3545',
      inputValidator: (v) => !v ? 'Vous devez entrer un motif !' : null
    });
    if (commentaire) {
      try {
        const token = localStorage.getItem('token');
        await axios.put(`${API_URL}/demandes-devis/${demande._id}/rejeter`,
          {commentaireAdmin: commentaire},
          {headers: {Authorization: `Bearer ${token}`}}
        );
        Swal.fire('Succès', 'Demande rejetée', 'success');
        await refreshDemande();
      } catch (e) {
        Swal.fire('Erreur', 'Impossible de rejeter la demande', 'error');
      }
    }
  };

  const handleSupprimer = async () => {
    const result = await Swal.fire({
      title: 'Êtes-vous sûr ?',
      text: 'Cette action est irréversible',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#dc3545'
    });
    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API_URL}/demandes-devis/${demande._id}`, {
          headers: {Authorization: `Bearer ${token}`}
        });
        Swal.fire('Supprimé', 'Demande supprimée avec succès', 'success');
        onBack();
      } catch (e) {
        Swal.fire('Erreur', 'Impossible de supprimer la demande', 'error');
      }
    }
  };

  const handleUploadContract = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const actuel = demande?.contratsClient?.length || 0;
    const requis = demande?.nombreContratsRequis || 1;

    if (actuel + files.length > requis) {
      Swal.fire({icon: 'warning', title: 'Nombre dépassé',
        text: `Cette demande nécessite ${requis} contrat(s). Il y en a déjà ${actuel}.`});
      return;
    }
    const invalides = files.filter(f => f.type !== 'application/pdf');
    if (invalides.length) {
      Swal.fire({icon: 'error', title: 'Format invalide', text: 'Seuls les PDF sont acceptés.'});
      return;
    }

    setUploadingContract(true);
    try {
      const token = localStorage.getItem('token');
      for (const file of files) {
        const fd = new FormData();
        fd.append('contrat', file);
        await axios.post(`${API_URL}/demandes-devis/${demande._id}/upload-contrat`, fd, {
          headers: {Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data'}
        });
      }
      Swal.fire({icon: 'success', title: 'Contrat(s) téléversé(s)', text: 'Ajoutés avec succès.'});
      await refreshDemande();
      e.target.value = null;
    } catch (err) {
      Swal.fire({icon: 'error', title: 'Erreur', text: err.response?.data?.message || 'Erreur upload.'});
    } finally {
      setUploadingContract(false);
    }
  };

  const handleDeleteContract = async (contractId) => {
    const result = await Swal.fire({
      icon: 'warning', title: 'Supprimer ce contrat ?',
      text: 'Cette action est irréversible.',
      showCancelButton: true, confirmButtonText: 'Supprimer',
      cancelButtonText: 'Annuler', confirmButtonColor: '#f56565'
    });
    if (!result.isConfirmed) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/demandes-devis/${demande._id}/contrat/${contractId}`,
        {headers: {Authorization: `Bearer ${token}`}}
      );
      Swal.fire({icon: 'success', title: 'Contrat supprimé'});
      await refreshDemande();
    } catch (err) {
      Swal.fire({icon: 'error', title: 'Erreur', text: err.response?.data?.message || 'Erreur.'});
    }
  };

  const statutColor = getStatutColor(demande.statut);
  const contratsDeposes = demande.contratsClient?.length || 0;
  const contratsRequis = demande.nombreContratsRequis || 1;
  const contratsComplets = contratsDeposes >= contratsRequis;

  return (
    <div className="ddd-page">
      <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />

      <div className="ddd-content">

        {/* ── HEADER ── */}
        <div className="ddd-hero">
          <div className="ddd-hero-left">
            <button className="ddd-btn-back" onClick={onBack}>
              ← Retour à la liste
            </button>
            <div className="ddd-hero-title-block">
              <h1>{demande.nom} {demande.prenom}</h1>
              <p className="ddd-hero-meta">
                <span>📧 {demande.mail}</span>
                <span>📅 {formatDate(demande.createdAt)}</span>
              </p>
            </div>
          </div>
          <div className="ddd-hero-badge-block">
            <span className="ddd-statut-hero-badge" style={{backgroundColor: statutColor}}>
              {getStatutLabel(demande.statut)}
            </span>
            {demande.commentaireAdmin && (
              <div className="ddd-hero-admin-note">
                💬 {demande.commentaireAdmin}
              </div>
            )}
          </div>
        </div>

        {/* ── ACTIONS BAR ── */}
        <div className="ddd-actions-bar">
          {demande.statut === 'en_attente' && hasPermission('devis', 'update') && (
            <>
              <button className="ddd-action-btn ddd-btn-validate" onClick={handleValider}>
                ✓ Valider la demande
              </button>
              <button className="ddd-action-btn ddd-btn-reject" onClick={handleRejeter}>
                ✗ Rejeter la demande
              </button>
            </>
          )}
          {hasPermission('devis', 'delete') && (
            <button className="ddd-action-btn ddd-btn-delete" onClick={handleSupprimer}>
              🗑️ Supprimer
            </button>
          )}
        </div>

        {/* ── BODY GRID ── */}
        <div className="ddd-grid">

          {/* ── COL GAUCHE ── */}
          <div className="ddd-col">

            {/* Informations Client */}
            <div className="ddd-card">
              <div className="ddd-card-header">
                <span className="ddd-card-icon">👤</span>
                <h2>Informations Client</h2>
              </div>
              <div className="ddd-card-body">
                <div className="ddd-info-row">
                  <span className="ddd-info-label">Nom complet</span>
                  <span className="ddd-info-value">{demande.nom} {demande.prenom}</span>
                </div>
                <div className="ddd-info-row">
                  <span className="ddd-info-label">Email</span>
                  <a href={`mailto:${demande.mail}`} className="ddd-info-value ddd-info-link">{demande.mail}</a>
                </div>
                <div className="ddd-info-row">
                  <span className="ddd-info-label">Téléphone</span>
                  <span className="ddd-info-value">{demande.idUser?.numeroTelephone || '—'}</span>
                </div>
                <div className="ddd-info-row">
                  <span className="ddd-info-label">Date de demande</span>
                  <span className="ddd-info-value">{formatDateLong(demande.createdAt)}</span>
                </div>
                {demande.dateValidation && (
                  <div className="ddd-info-row">
                    <span className="ddd-info-label">Date de validation</span>
                    <span className="ddd-info-value">{formatDateLong(demande.dateValidation)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Consommation */}
            <div className="ddd-card">
              <div className="ddd-card-header">
                <span className="ddd-card-icon">⚡</span>
                <h2>Consommation Électrique</h2>
              </div>
              <div className="ddd-card-body">
                <div className="ddd-info-row">
                  <span className="ddd-info-label">Consommation mensuelle</span>
                  <span className="ddd-info-value ddd-info-highlight">{demande.consommationMensuelle} kWh</span>
                </div>
                <div className="ddd-info-row">
                  <span className="ddd-info-label">Période</span>
                  <span className="ddd-info-value ddd-capitalize">{demande.periodeConsommation}</span>
                </div>
                <div className="ddd-info-row">
                  <span className="ddd-info-label">Consommation annuelle</span>
                  <span className="ddd-info-value ddd-info-highlight">{demande.consommationAnnuelle} kWh/an</span>
                </div>
                <div className="ddd-info-row">
                  <span className="ddd-info-label">Pompage</span>
                  <span className={`ddd-badge ${demande.pompage ? 'ddd-badge-yes' : 'ddd-badge-no'}`}>
                    {demande.pompage ? 'Oui' : 'Non'}
                  </span>
                </div>
                {demande.pompage && (
                  <div className="ddd-info-row">
                    <span className="ddd-info-label">Type de pompage</span>
                    <span className={`ddd-badge ${demande.typePompage === 'autonome_sans_steg' ? 'ddd-badge-solar' : 'ddd-badge-steg'}`}>
                      {demande.typePompage === 'autonome_sans_steg' ? '🌞 Autonome (sans STEG)' : '⚡ Avec STEG'}
                    </span>
                  </div>
                )}
                {demande.pompage && demande.typePompage === 'avec_steg' && (
                  <div className="ddd-info-row">
                    <span className="ddd-info-label">Consommation pompage</span>
                    <span className="ddd-info-value">{demande.consommationPompage} kWh</span>
                  </div>
                )}
              </div>
            </div>

            {/* Puissance */}
            <div className="ddd-card">
              <div className="ddd-card-header">
                <span className="ddd-card-icon">🔌</span>
                <h2>Calcul de Puissance</h2>
              </div>
              <div className="ddd-card-body">
                <div className="ddd-power-summary">
                  <div className="ddd-power-item">
                    <span className="ddd-power-label">Puissance nécessaire</span>
                    <span className="ddd-power-value">{demande.puissanceNecessaire} <small>kWc</small></span>
                  </div>
                  <div className="ddd-power-divider">→</div>
                  <div className="ddd-power-item">
                    <span className="ddd-power-label">Puissance commerciale</span>
                    <span className="ddd-power-value ddd-power-main">{demande.puissanceCommerciale} <small>kWc</small></span>
                  </div>
                </div>
              </div>
            </div>

            {/* Avis & Perspectives */}
            {(demande.avisPhotovoltaique || demande.perspective) && (
              <div className="ddd-card">
                <div className="ddd-card-header">
                  <span className="ddd-card-icon">💭</span>
                  <h2>Avis &amp; Perspectives</h2>
                </div>
                <div className="ddd-card-body">
                  {demande.avisPhotovoltaique && (
                    <div className="ddd-info-block">
                      <span className="ddd-info-label">Avis sur le photovoltaïque</span>
                      <p className="ddd-info-text">{demande.avisPhotovoltaique}</p>
                    </div>
                  )}
                  {demande.perspective && (
                    <div className="ddd-info-block">
                      <span className="ddd-info-label">Perspective</span>
                      <p className="ddd-info-text">{demande.perspective}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ── COL DROITE ── */}
          <div className="ddd-col">

            {/* Statut */}
            <div className="ddd-card">
              <div className="ddd-card-header">
                <span className="ddd-card-icon">📊</span>
                <h2>Statut de la Demande</h2>
              </div>
              <div className="ddd-card-body">
                <div className="ddd-statut-block">
                  <span className="ddd-statut-badge-large" style={{backgroundColor: statutColor}}>
                    {getStatutLabel(demande.statut)}
                  </span>
                </div>
                {demande.commentaireAdmin && (
                  <div className="ddd-admin-comment-block">
                    <span className="ddd-info-label">Commentaire Admin</span>
                    <p className="ddd-admin-comment-text">{demande.commentaireAdmin}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Contrat */}
            <div className="ddd-card">
              <div className="ddd-card-header">
                <span className="ddd-card-icon">📋</span>
                <h2>Informations Contrat</h2>
              </div>
              <div className="ddd-card-body">
                <div className="ddd-info-row">
                  <span className="ddd-info-label">Type de contrat</span>
                  <span className={`ddd-contract-badge ${demande.typeContrat === 'credit_prosol' ? 'prosol' : 'hors'}`}>
                    {demande.typeContrat === 'credit_prosol' ? 'Crédit PROSOL' : 'Hors PROSOL'}
                  </span>
                </div>
                <div className="ddd-info-row">
                  <span className="ddd-info-label">Mode de paiement</span>
                  <span className="ddd-info-value ddd-capitalize">{demande.modePaiement}</span>
                </div>
                <div className="ddd-info-row">
                  <span className="ddd-info-label">Type formulaire PROSOL</span>
                  <span className="ddd-info-value">{demande.typeFormulaireProsol || '—'}</span>
                </div>
                <div className="ddd-info-row">
                  <span className="ddd-info-label">Prix formulaire</span>
                  <span className="ddd-info-value ddd-info-highlight">{demande.prixFormulaire} DT</span>
                </div>
              </div>
            </div>

            {/* Contrats Signés */}
            <div className="ddd-card">
              <div className="ddd-card-header">
                <span className="ddd-card-icon">📄</span>
                <h2>Contrats Signés</h2>
                <span className={`ddd-contrats-counter ${contratsComplets ? 'ddd-counter-ok' : 'ddd-counter-pending'}`}>
                  {contratsDeposes}/{contratsRequis}
                </span>
              </div>
              <div className="ddd-card-body">
                {contratsComplets && (
                  <div className="ddd-alert-success">
                    ✅ Tous les contrats requis ont été déposés
                  </div>
                )}

                {demande.contratsClient && demande.contratsClient.length > 0 ? (
                  <div className="ddd-contrats-list">
                    {demande.contratsClient.map((contrat, idx) => (
                      <div key={idx} className="ddd-contrat-item">
                        <div className="ddd-contrat-item-left">
                          <span className="ddd-contrat-icon">📑</span>
                          <div>
                            <div className="ddd-contrat-name">{contrat.nomFichier}</div>
                            <div className="ddd-contrat-date">
                              Déposé le {new Date(contrat.dateUpload).toLocaleDateString('fr-FR', {
                                year: 'numeric', month: 'long', day: 'numeric',
                                hour: '2-digit', minute: '2-digit'
                              })}
                            </div>
                          </div>
                        </div>
                        <div className="ddd-contrat-item-actions">
                          <button
                            className="ddd-btn-contrat ddd-btn-open"
                            onClick={() => {
                              const token = localStorage.getItem('token');
                              window.open(`${API_URL}/demandes-devis/${demande._id}/contrat/${contrat._id}/download?token=${token}`, '_blank');
                            }}
                          >
                            📂 Ouvrir
                          </button>
                          {demande.statut === 'en_cours' && hasPermission('devis', 'update') && (
                            <button
                              className="ddd-btn-contrat ddd-btn-del-contrat"
                              onClick={() => handleDeleteContract(contrat._id)}
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="ddd-alert-warning">
                    ⚠️ Aucun contrat n'a encore été déposé par le client
                  </div>
                )}

                {/* Zone upload admin */}
                {demande.statut === 'en_cours' &&
                  hasPermission('devis', 'update') &&
                  contratsDeposes < contratsRequis && (
                    <div className="ddd-upload-zone">
                      <h4 className="ddd-upload-title">👨‍💼 Ajouter un contrat (Admin)</h4>
                      <p className="ddd-upload-hint">
                        Vous pouvez ajouter les contrats signés au nom du client si statut "En cours".
                      </p>
                      <label className="ddd-upload-label" htmlFor="ddd-admin-upload">
                        <div className="ddd-upload-dropzone">
                          <span className="ddd-upload-icon">📤</span>
                          <span className="ddd-upload-text">
                            {uploadingContract ? 'Téléversement en cours...' : 'Cliquez pour téléverser'}
                          </span>
                          <span className="ddd-upload-sub">Format PDF uniquement</span>
                        </div>
                        <input
                          id="ddd-admin-upload"
                          type="file"
                          accept="application/pdf"
                          multiple={contratsRequis > 1}
                          onChange={handleUploadContract}
                          disabled={uploadingContract}
                          style={{display: 'none'}}
                        />
                      </label>
                    </div>
                  )}
              </div>
            </div>

            {/* Section Pompage Solaire Autonome */}
            {demande.pompage && demande.typePompage === 'autonome_sans_steg' && (
              <div className="ddd-card ddd-card-solar">
                <div className="ddd-card-header">
                  <span className="ddd-card-icon">🌞</span>
                  <h2>Pompage Solaire Autonome</h2>
                </div>
                <div className="ddd-card-body">
                  <div className="ddd-info-row">
                    <span className="ddd-info-label">Surface à irriguer</span>
                    <span className="ddd-info-value ddd-info-highlight">{demande.surfaceHectares || '—'} ha</span>
                  </div>
                  <div className="ddd-info-row">
                    <span className="ddd-info-label">Débit souhaité</span>
                    <span className="ddd-info-value ddd-info-highlight">{demande.debitSouhaite || '—'} m³/jour</span>
                  </div>
                  <div className="ddd-info-row">
                    <span className="ddd-info-label">Hauteur de refoulement</span>
                    <span className="ddd-info-value ddd-info-highlight">{demande.hauteurRefoulement || '—'} m</span>
                  </div>
                  <div className="ddd-info-row">
                    <span className="ddd-info-label">Type de culture</span>
                    <span className="ddd-info-value">{demande.typeCulture || '—'}</span>
                  </div>
                  <div className="ddd-info-row">
                    <span className="ddd-info-label">Budget prévu</span>
                    <span className="ddd-info-value ddd-info-highlight">
                      {demande.budgetPompage ? `${demande.budgetPompage.toLocaleString()} DT` : '—'}
                    </span>
                  </div>

                  {/* Systèmes recommandés */}
                  {demande.systemesPompageRecommandes?.length > 0 && (
                    <div className="ddd-systemes-section">
                      <h4 className="ddd-systemes-title">⚙️ Systèmes Recommandés</h4>
                      {demande.systemesPompageRecommandes.map((sys, idx) => (
                        <div key={idx} className={`ddd-systeme-card ${idx === 0 ? 'ddd-systeme-top' : ''}`}>
                          {idx === 0 && <span className="ddd-systeme-top-badge">⭐ Recommandé</span>}
                          <div className="ddd-systeme-header">
                            <div>
                              <div className="ddd-systeme-name">{sys.puissanceMoteur} CV → {sys.puissanceSolaire} kWc</div>
                              <div className="ddd-systeme-desc">{sys.description}</div>
                            </div>
                            <div className="ddd-systeme-cost">
                              <div className="ddd-systeme-price">{sys.coutInstallation?.toLocaleString()} DT</div>
                              {sys.scenario && (
                                <span className={`ddd-badge-scenario ddd-badge-${sys.scenario}`}>
                                  {sys.scenario === 'economique' && '💵 Économique'}
                                  {sys.scenario === 'equilibre' && '⚖️ Équilibré'}
                                  {sys.scenario === 'performance' && '🚀 Performance'}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="ddd-systeme-details">
                            <div className="ddd-sys-detail"><span>💧</span><span><strong>Débit:</strong> {sys.debitMoyen} m³/j</span></div>
                            <div className="ddd-sys-detail"><span>📏</span><span><strong>Hauteur:</strong> {sys.hauteurRefoulementMax}m</span></div>
                            <div className="ddd-sys-detail"><span>🌾</span><span><strong>Surface:</strong> {sys.surfaceIrriguee}</span></div>
                            <div className="ddd-sys-detail"><span>💰</span><span><strong>Économies:</strong> {sys.economieAnnuelle} DT/an</span></div>
                            <div className="ddd-sys-detail"><span>📊</span><span><strong>ROI:</strong> {sys.retourInvestissement} ans</span></div>
                          </div>
                          {demande.systemeChoisi === sys.systemeId && (
                            <div className="ddd-systeme-choisi-badge">✓ Système choisi par le client</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

          </div>{/* fin col droite */}
        </div>{/* fin grid */}
      </div>{/* fin content */}
    </div>
  );
};

export default DetailDemandesDevis;
